import { useState } from "react";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "./firebaseConfig";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Header from "./components/Header";

/* -------------------- Friendly Error Mapper -------------------- */
function getFriendlyErrorMessage(code) {
  switch (code) {
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/user-disabled":
      return "This account has been disabled. Contact support if you think this is an error.";
    case "auth/user-not-found":
      return "No account found with that email.";
    case "auth/wrong-password":
      return "Incorrect password. Please try again.";
    case "auth/email-already-in-use":
      return "This email is already registered. Try logging in instead.";
    case "auth/weak-password":
      return "Your password should be at least 6 characters.";
    case "auth/popup-closed-by-user":
      return "Google sign-in was cancelled.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection.";
    default:
      return "Something went wrong. Please try again.";
  }
}

/* -------------------- MAIN COMPONENT -------------------- */
export default function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(getFriendlyErrorMessage(err.code));
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setMessage("");
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      navigate(from, { replace: true });
    } catch (err) {
      setError(getFriendlyErrorMessage(err.code));
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Please enter your email above first.");
      return;
    }
    setError("");
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("✅ Password reset email sent! Check your inbox.");
    } catch (err) {
      setError(getFriendlyErrorMessage(err.code));
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col text-gray-800 overflow-hidden">
      {/* === FIXED LIGHT BACKGROUND === */}
      <div className="fixed inset-0 -z-10" style={{ background: 'linear-gradient(to bottom, #FFFADF 0%, rgba(255, 250, 223, 0.8) 50%, #FFFADF 100%)' }} />
      <div className="fixed inset-0 -z-10" style={{ background: 'radial-gradient(circle at 50% 20%, rgba(143, 179, 211, 0.12), transparent 60%)' }} />
      <div
        className="fixed inset-0 -z-10 opacity-[.04] pointer-events-none"
        style={{ backgroundImage: "url(/images/noise-512.png)" }}
      />

      {/* --- Global Header --- */}
      <Header />

      {/* --- Content --- */}
      <div className="flex-grow flex items-center justify-center px-4 mt-20 mb-12 relative z-10">
        <div className="w-full max-w-md bg-white border border-gray-200 rounded-3xl shadow-md p-8 md:p-10">
          <h2 className="text-3xl font-semibold text-center text-gray-900 mb-2">
            Welcome Back
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Log in to access your tours
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full p-3 rounded-lg border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition"
              style={{ borderColor: '#E4B4A6', '--tw-ring-color': 'rgba(143, 179, 211, 0.6)' }}
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full p-3 rounded-lg border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition"
              style={{ borderColor: '#E4B4A6', '--tw-ring-color': 'rgba(143, 179, 211, 0.6)' }}
            />

            <div className="text-right -mt-1">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm font-semibold transition hover:opacity-70"
                style={{ color: '#F8662B' }}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              className="w-full py-3 rounded-lg font-semibold text-white shadow-md hover:shadow-lg active:scale-95 transition-all hover:opacity-90"
              style={{ backgroundColor: '#F8662B' }}
            >
              Log In
            </button>
          </form>

          <div className="my-6 text-center text-gray-400 text-sm">or</div>

          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-all font-semibold shadow-sm"
          >
            <img
              src="https://www.svgrepo.com/show/355037/google.svg"
              alt="Google"
              className="w-5 h-5"
            />
            Continue with Google
          </button>

          {error && (
            <p className="text-red-500 mt-4 text-sm text-center font-medium">
              {error}
            </p>
          )}
          {message && (
            <p className="text-green-600 mt-4 text-sm text-center font-medium">
              {message}
            </p>
          )}

          <p className="text-center text-gray-700 mt-6 text-sm">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="font-semibold transition hover:opacity-70"
              style={{ color: '#F8662B' }}
            >
              Sign up here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
